import type { ApolloCache } from '@apollo/client';

import {
  GET_SETTINGS_TEAM_DETAILS,
  GET_SETTINGS_TEAM_SKILLS,
  GET_SETTINGS_USER_GROUPS,
  type SettingsTeamDetails,
} from '../../../queries/skill-administration';
import { GET_CUSTOM_SKILL_GROUP_SHARES, type SkillShareGroup } from '../../../queries/skill-group-sharing';

export function updateCachedTeamDetails(
  cache: ApolloCache<unknown>,
  groupId: string,
  patch: Partial<SettingsTeamDetails>,
) {
  cache.updateQuery({ query: GET_SETTINGS_TEAM_DETAILS, variables: { groupId } }, (current) =>
    current ? { ...current, getSkillGroup: { ...current.getSkillGroup, ...patch } } : current,
  );
}

type SkillAccessChange =
  | { kind: 'share'; group: SkillShareGroup; skillUuid: string }
  | { kind: 'revoke'; groupId: string; skillUuid: string };

/** Both sharing entry points update both sides of the relationship in one cache transaction. */
export function updateCachedSkillGroupAccess(cache: ApolloCache<unknown>, change: SkillAccessChange) {
  const groupId = change.kind === 'share' ? change.group.uuid : change.groupId;
  const { skillUuid } = change;
  cache.batch({
    update() {
      cache.updateQuery({ query: GET_SETTINGS_TEAM_DETAILS, variables: { groupId } }, (current) => {
        if (!current) return current;
        const group = current.getSkillGroup;
        const skillUuids = group.skillUuids.filter((uuid) => uuid !== skillUuid);
        if (change.kind === 'share') skillUuids.push(skillUuid);
        return { ...current, getSkillGroup: { ...group, skillUuids } };
      });
      cache.updateQuery({ query: GET_SETTINGS_TEAM_SKILLS, variables: { groupId } }, (current) => {
        if (!current || change.kind === 'share') return current;
        const skills = current.getSkillGroup.skills.filter((skill) => skill.uuid !== skillUuid);

        return { ...current, getSkillGroup: { ...current.getSkillGroup, skills } };
      });
      cache.updateQuery({ query: GET_CUSTOM_SKILL_GROUP_SHARES, variables: { skillUuid } }, (current) => {
        if (!current) return current;
        const groups = current.getCustomSkillGroupAssignments.groups.filter((group) => group.uuid !== groupId);
        if (change.kind === 'share') {
          // Discovery uses a different GraphQL type from skill assignments.
          const sharedGroup = { ...change.group, __typename: 'SkillGroupSummary' };
          groups.push(sharedGroup);
        }
        return { ...current, getCustomSkillGroupAssignments: { ...current.getCustomSkillGroupAssignments, groups } };
      });
    },
  });
}

export function updateCachedGroupRoles(
  cache: ApolloCache<unknown>,
  group: { uuid: string; admins: string[]; managers: string[] },
) {
  cache.updateQuery({ query: GET_SETTINGS_USER_GROUPS }, (current) =>
    current
      ? {
          ...current,
          getUserNovaGroups: current.getUserNovaGroups.map((item) =>
            item.uuid === group.uuid ? { ...item, admins: group.admins, managers: group.managers } : item,
          ),
        }
      : current,
  );
}

export function removeTeamFromCache(cache: ApolloCache<unknown>, groupId: string) {
  cache.batch({
    update() {
      cache.updateQuery({ query: GET_SETTINGS_USER_GROUPS }, (current) =>
        current
          ? { ...current, getUserNovaGroups: current.getUserNovaGroups.filter((item) => item.uuid !== groupId) }
          : current,
      );
      cache.modify({
        id: 'ROOT_QUERY',
        fields: {
          getCustomSkillGroupAssignments(existing, { readField }) {
            return {
              ...existing,
              groups: existing.groups.filter((item: { __ref?: string }) => readField('uuid', item) !== groupId),
            };
          },
        },
      });
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'getSkillGroup', args: { groupId } });
      for (const typename of ['SkillGroup', 'SkillGroupSummary', 'SkillAdministrationGroup']) {
        const id = cache.identify({ __typename: typename, uuid: groupId });
        if (id) cache.evict({ id });
      }
    },
  });
}
